import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  InjectionToken,
  INJECTOR,
  OnInit,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  Einheiteneingabe,
  EinheiteneingabeEinheitMenge,
} from '../types/einheiteneingabe';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { chooseFn, Modal, UiCoreModule } from '@softline/ui-core';
import {
  BehaviorSubject,
  catchError,
  concatMap,
  filter,
  firstValueFrom,
  of,
  pairwise,
  startWith,
  switchMap,
} from 'rxjs';
import { map } from 'rxjs/operators';
import { EinheiteneingabeService } from '../services/einheiteneingabe.service';
import { handleRequestError } from '@softline/application';
import { ConnectionHttpService } from '@softline/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Dimension } from '../types/dimension';
import { FieldOkService } from '@softline/dynamic';

export interface EinheiteneingabeParams {
  idartstamm: number;
  menge: number;
  idbasisEinheit: number;
}

const SOFTLINE_TOKEN_UNIT_ENTRY_SERVICE = new InjectionToken<
  FieldOkService<any, any, any>
>('SOFTLINE_TOKEN_UNIT_ENTRY_SERVICE');

@Component({
  selector: 'soft-einheiteneingabe-dialog',
  imports: [CommonModule, ReactiveFormsModule, UiCoreModule],
  templateUrl: './einheiteneingabe-dialog.component.html',
  styleUrls: ['./einheiteneingabe-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: SOFTLINE_TOKEN_UNIT_ENTRY_SERVICE,
      useFactory: () => new FieldOkService(inject(ConnectionHttpService), 'masseinheitd'),
    },
  ],
})
export class EinheiteneingabeDialogComponent
  implements OnInit, Modal<Einheiteneingabe | null>
{
  private injector = inject(INJECTOR);
  private choose = chooseFn();
  private _params$ = new BehaviorSubject<EinheiteneingabeParams | null>(null);
  private fokService = inject(SOFTLINE_TOKEN_UNIT_ENTRY_SERVICE);

  readonly selectedFormular = signal<any | null>(null);

  private readonly value$ = this._params$.pipe(
    filter((o): o is EinheiteneingabeParams => !!o),
    switchMap((params) =>
      this.service.initialisieren(params).pipe(
        map((value) => this.createForm(value)),
        switchMap((form) => {
          this.loading.set(false);

          return form.valueChanges.pipe(
            startWith(form.value),
            pairwise(),
            map(([previous, current]) => this.computeFormState(previous, current)),
            concatMap((formState: Einheiteneingabe) =>
              this.service
                .umrechnen({
                  ...formState,
                  erfassungsEinheit: {
                    einheit: {
                      id: params.idbasisEinheit,
                    } as EinheiteneingabeEinheitMenge['einheit'],
                    menge: 0,
                  } as EinheiteneingabeEinheitMenge,
                })
                .pipe(
                  catchError((e) => {
                    handleRequestError(e, this.injector, { show: true });
                    this.loading.set(false);
                    return of(null);
                  })
                )
            )
          );
        }),
        catchError((e) => {
          handleRequestError(e, this.injector, { show: true });
          this.loading.set(false);
          return of(null);
        })
      )
    )
  );

  form?: FormGroup;
  close!: (result: Einheiteneingabe | null) => void;

  readonly loading = signal(false);

  set params(params: EinheiteneingabeParams) {
    this.loading.set(true);
    this._params$.next(params);
  }

  constructor(
    private readonly fb: FormBuilder,
    private readonly destroyRef: DestroyRef,
    private readonly cdRef: ChangeDetectorRef,
    private readonly service: EinheiteneingabeService
  ) {}

  ngOnInit(): void {
    this.value$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((newFormState) => {
      this.form?.patchValue(newFormState as never, { emitEvent: false });
      this.cdRef.markForCheck();
    });
  }

  registerCloseHandler(handler: (result: Einheiteneingabe | null) => void) {
    this.close = handler;
  }

  retry(): void {
    this._params$.next(this._params$.value);
  }

  private createForm(value: Einheiteneingabe): FormGroup {
    this.form = this.fb.nonNullable.group({
      artikel: [value.artikel],
      ideinheitEingabe: [null],
      summeEinheit: this.createEinheitMengeFormGroup(value.summeEinheit),
      erfassungsEinheit: this.createEinheitMengeFormGroup(value.erfassungsEinheit),
      einheiten: this.fb.array<FormGroup>(
        value.einheiten.map((mengeEinheit) =>
          this.createEinheitMengeFormGroup(mengeEinheit)
        )
      ),
      dimensionen: value?.dimensionen
        ? this.createDimensionFormGroup(value.dimensionen)
        : [null],
    });

    return this.form!;
  }

  private createEinheitMengeFormGroup(
    mengeEinheit: EinheiteneingabeEinheitMenge,
    editable = true
  ): FormGroup {
    return new FormGroup({
      einheit: new FormControl(mengeEinheit.einheit),
      menge: new FormControl(mengeEinheit?.menge as never),
      editable: new FormControl(editable, { nonNullable: true }),
    });
  }

  private createDimensionFormGroup(dimensions: Dimension): FormGroup {
    return new FormGroup({
      tiefeBez: new FormControl<string | null>(dimensions?.tiefeBez ?? null),
      tiefe: new FormControl<number | null>(dimensions?.tiefe),
      hoeheBez: new FormControl<string | null>(dimensions?.hoeheBez ?? null),
      hoehe: new FormControl<number | null>(dimensions?.hoehe),
      breiteBez: new FormControl<string | null>(dimensions?.breiteBez ?? null),
      breite: new FormControl<number | null>(dimensions?.breite),
    });
  }

  private computeFormState(
    previousFormValue: Einheiteneingabe,
    currentFormValue: Einheiteneingabe
  ): Einheiteneingabe {
    let change: EinheiteneingabeEinheitMenge | null = null;

    for (const [index, einheitMenge] of previousFormValue['einheiten'].entries()) {
      const currentEinheitMenge = currentFormValue['einheiten'][index];
      if (einheitMenge.menge !== currentEinheitMenge?.menge) {
        change = currentEinheitMenge;
        break;
      }
    }

    return {
      ...currentFormValue,
      ideinheitEingabe: change?.einheit?.id,
    };
  }

  async openFormularModal(): Promise<void> {
    const response = await firstValueFrom(
      this.fokService.query({
        filter: '',
        multiValued: false,
        parameters: {},
      })
    );

    const modalResult = await this.choose({
      options: response.map((res) => {
        return {
          value: res,
          label: res?.begriff ?? `${res.id}`,
        };
      }),
    });

    if (!modalResult || modalResult === 'DISMISSED') return;

    console.log('result: ');
    this.selectedFormular.set(modalResult);
  }
}
